import { useAuth0 } from "@auth0/auth0-react";
import {
  AppContext,
  AppContextValues,
  NavigationBlockerDetails,
} from "AppContext";
import {
  EnumActivityLogType,
  EnumEntityReferenceType,
  EnumOrganizationMetadataDisabledApps,
  EnumOwnerType,
  EnumUserAccessOperatorsRole,
  EnumUserAccessOperatorsStatus,
  EnumUserUserType,
  useActivityLogCreateMutation,
  useBroadcastByUserLazyQuery,
  useOperatorsByUserLazyQuery,
  useOrganizationsByIdsLazyQuery,
  useOrganizationsByUserLazyQuery,
  usePersonFindOneNameQuery,
  UserAccessOperators,
  UserFindOneDocument,
  useStorefrontsByUserLazyQuery,
  useUserFindSelfQuery,
  useUserJoinOrganizationFromColdSignupMutation,
  useUserUpdateMutation,
} from "gql/generated";
import { consumeColdSignupOrganizationId } from "shared/utils";
import _ from "lodash";
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { TNB_INTERNAL_OWNER_ID } from "shared/constants";
import {
  useDocumentTitle,
  useLoggedInAs,
  useLoginRedirectPath,
  usePostHog,
} from "shared/hooks";
import { getUserCategory } from "shared/hooks/usePostHog";
import { useImpersonation } from "shared/contextProviders/impersonation";
import { useLastLogin } from "shared/hooks/useLastLogin";
import {
  useHasDirtyForms,
  useRegisterDirtyForm,
} from "shared/hooks/useDirtyFormRegistry";
import { AUTH_SESSION_LOGOUT_REASON_KEY } from "shared/constants/authSession";

/** Normalize entityId to a string for reliable comparison (API may return string or MongoID scalar). */
function normalizeEntityId(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (
    typeof value === "object" &&
    value !== null &&
    "$oid" in value &&
    typeof (value as { $oid: string }).$oid === "string"
  ) {
    return (value as { $oid: string }).$oid;
  }
  return String(value);
}

export const AppContextProvider = ({ children }: PropsWithChildren) => {
  useDocumentTitle();

  /**
   * There are 5 items required to load for `isInitialized` to be true
   * 1. dbUser
   * 2. operators
   * 3. organizations
   * 4. storefronts
   * 5. broadcasts
   * - Once these are loaded, they are NOT reset to false during refetches
   * - This ensures the full screen loader is ONLY shown on initial load
   */
  const [initializedItems, setInitializedItems] = useState<{
    dbUser: boolean;
    operators: boolean;
    organizations: boolean;
    storefronts: boolean;
    broadcasts: boolean;
  }>({
    dbUser: false,
    operators: false,
    organizations: false,
    storefronts: false,
    broadcasts: false,
  });

  /**
   * Load the user, their operators, and their organizations
   */
  const { user, logout } = useAuth0();
  const auth0Id = user?.sub ?? "";
  const { impersonatedUserId } = useImpersonation();

  /**
   * If a user is logged in and then returns to the base route (/) and enters
   * a different email address, we need to ensure the currently logged in user
   * is logged out and that the new user is prompted to login
   *
   * The code below runs in a useEffect because if it doesn't the onboarding
   * screen redraws/reanimates during the logout phase
   */
  const { clearLoginRedirectPath, getLoginRedirectSearchParams } =
    useLoginRedirectPath();
  const [isForcingLogout, setIsForcingLogout] = useState(false);
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  useEffect(() => {
    if (impersonatedUserId) return;
    if (user?.email && email && user.email !== email) {
      setIsForcingLogout(true);
      clearLoginRedirectPath();

      const returnParams = new URLSearchParams();
      returnParams.set("email", email.toLowerCase());

      const storedSearch = getLoginRedirectSearchParams();
      if (storedSearch) {
        const storedParams = new URLSearchParams(
          storedSearch.startsWith("?") ? storedSearch.slice(1) : storedSearch,
        );
        const broadcast = storedParams.get("broadcast");
        if (broadcast) {
          returnParams.set("broadcast", broadcast);
        }
      } else {
        const broadcast = searchParams.get("broadcast");
        if (broadcast) {
          returnParams.set("broadcast", broadcast);
        }
      }

      logout({
        logoutParams: {
          returnTo: `${window.location.origin}/app?${returnParams.toString()}`,
        },
      });
    }
  }, [
    email,
    user,
    impersonatedUserId,
    clearLoginRedirectPath,
    getLoginRedirectSearchParams,
    logout,
    searchParams,
    setIsForcingLogout,
  ]);

  // Get the User record from MongoDB (or the impersonated user when active)
  const userFilter = impersonatedUserId
    ? { _id: impersonatedUserId }
    : { auth0Id };
  const {
    data: dbUser,
    error: dbUserError,
    loading: dbUserLoading,
    refetch: refetchDbUser,
  } = useUserFindSelfQuery({
    variables: { filter: userFilter },
    onCompleted: () => setInitializedItems(prev => ({ ...prev, dbUser: true })),
    skip: !impersonatedUserId && !auth0Id,
  });

  // Clear forced-logout notice once a valid app user has loaded
  useEffect(() => {
    if (dbUser?.userFindOne) {
      sessionStorage.removeItem(AUTH_SESSION_LOGOUT_REASON_KEY);
    }
  }, [dbUser?.userFindOne]);

  // Get the Person record from MongoDB
  const {
    data: dbPerson,
    loading: dbPersonLoading,
    refetch: refetchDbPerson,
  } = usePersonFindOneNameQuery({
    variables: {
      filter: {
        user: dbUser?.userFindOne?._id,
      },
    },
    skip: !dbUser || !dbUser.userFindOne || !dbUser.userFindOne._id,
  });

  // When the Person record is loaded, check to see if the user logged
  const { getLastLogin, updateLastLogin } = useLastLogin();
  const [createActivityLog] = useActivityLogCreateMutation();
  const { trackPostHogException, identifyPostHog, groupPostHog, registerPostHog, trackPostHogEvent } = usePostHog();
  useEffect(() => {
    if (!user || !dbUser?.userFindOne || !dbPerson?.personFindOne) return;

    const lastLogin = getLastLogin();
    if (lastLogin && !lastLogin.activityLogCreated) {
      const initiatedBy =
        dbPerson.personFindOne.firstName +
        " " +
        dbPerson.personFindOne.lastName;
      createActivityLog({
        variables: {
          record: {
            entity: {
              type: EnumEntityReferenceType.Person,
              _entityId: dbPerson.personFindOne._id,
            },
            owner: {
              type: EnumOwnerType.TnbInternal,
              entityId: TNB_INTERNAL_OWNER_ID,
            },
            initiatedBy,
            message: {
              en: `${initiatedBy} logged in`,
              es: `${initiatedBy} inició sesión`,
              fr: `${initiatedBy} a ouvert sa session`,
            },
            entityName: {
              en: initiatedBy,
              es: initiatedBy,
              fr: initiatedBy,
            },
            type: EnumActivityLogType.UserLoggedIn,
            initiatedByPerson: dbPerson.personFindOne._id,
            initiatedByUser: dbUser.userFindOne._id,
          },
        },
      });

      trackPostHogEvent("user_logged_in" as never, {
        userId: dbUser.userFindOne._id,
        personId: dbPerson.personFindOne._id,
      });

      updateLastLogin(true);
    }
  }, [
    user,
    dbUser?.userFindOne,
    dbPerson?.personFindOne,
    createActivityLog,
    getLastLogin,
    updateLastLogin,
    trackPostHogEvent,
  ]);

  const [
    getUserOrganizations,
    { data: organizations, error: orgError, loading: orgLoading },
  ] = useOrganizationsByUserLazyQuery();

  const [
    getUserOperators,
    { data: operators, error: opError, loading: opLoading },
  ] = useOperatorsByUserLazyQuery();

  const [
    getUserStorefronts,
    { data: storefronts, error: sfError, loading: sfLoading },
  ] = useStorefrontsByUserLazyQuery();

  const [
    getUserBroadcasts,
    { data: broadcasts, error: bcError, loading: bcLoading },
  ] = useBroadcastByUserLazyQuery();
  const [joinOrganizationFromColdSignup] =
    useUserJoinOrganizationFromColdSignupMutation();
  // Ref so a changing callback identity doesn't re-run the user data load effect
  const trackPostHogExceptionRef = useRef(trackPostHogException);
  trackPostHogExceptionRef.current = trackPostHogException;

  // Refetch the user's operators, organizations, storefronts, and broadcasts when the dbUser is updated
  useEffect(() => {
    if (!dbUser || !dbUser.userFindOne || !dbUser.userFindOne._id) return;

    getUserOperators({
      onCompleted: () =>
        setInitializedItems(prev => ({ ...prev, operators: true })),
    });
    getUserOrganizations({
      onCompleted: () =>
        setInitializedItems(prev => ({ ...prev, organizations: true })),
    });
    getUserStorefronts({
      onCompleted: () =>
        setInitializedItems(prev => ({ ...prev, storefronts: true })),
    });
    const loadBroadcasts = () =>
      getUserBroadcasts({
        onCompleted: () =>
          setInitializedItems(prev => ({ ...prev, broadcasts: true })),
      });

    // Join the cold sign up org (and get its sign up tag) before loading
    // broadcasts so tag-targeted broadcasts show on the first load
    const coldSignupOrganizationId = impersonatedUserId
      ? null
      : consumeColdSignupOrganizationId();
    if (coldSignupOrganizationId) {
      // Never hold up app initialization for more than a few seconds
      const timeout = new Promise(resolve => setTimeout(resolve, 5000));
      Promise.race([
        joinOrganizationFromColdSignup({
          variables: { organizationId: coldSignupOrganizationId },
        }),
        timeout,
      ])
        .catch(error =>
          trackPostHogExceptionRef.current(error as Error, {
            fileName: "AppContextProvider.tsx",
            method: "joinOrganizationFromColdSignup",
            message: "Error joining organization from cold sign up",
          }),
        )
        .finally(loadBroadcasts);
    } else {
      loadBroadcasts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- impersonation is read once per dbUser load
  }, [
    dbUser,
    user,
    getUserOperators,
    getUserOrganizations,
    getUserStorefronts,
    getUserBroadcasts,
    joinOrganizationFromColdSignup,
  ]);

  const refetchBroadcasts = useCallback(() => {
    getUserBroadcasts();
  }, [getUserBroadcasts]);

  // Get the connections for the operators the user is enrolled in
  const [
    getOperatorConnections,
    {
      data: operatorConnections,
      loading: operatorConnectionsLoading,
      error: operatorConnectionsError,
    },
  ] = useOrganizationsByIdsLazyQuery();
  useEffect(() => {
    if (operators?.operatorsByUser && operators.operatorsByUser.length > 0) {
      getOperatorConnections({
        variables: {
          ids: Array.from(
            new Set(
              operators.operatorsByUser.flatMap(operator =>
                operator.enrollments.map(
                  enrollment => enrollment?.organization?._id,
                ),
              ),
            ),
          ),
        },
      });
    }
  }, [operators?.operatorsByUser, getOperatorConnections]);

  /**
   * Ensure the user's status for the given entity is set to ACCEPTED
   * when the visit that entity's page (IF that entity is in the user's access)
   */
  const { organizationId, operatorEntityId, storefrontId } = useParams();
  const [userUpdateMutation] = useUserUpdateMutation({
    refetchQueries: [UserFindOneDocument],
  });
  useEffect(() => {
    if (
      !dbUser?.userFindOne ||
      (!organizationId && !operatorEntityId && !storefrontId)
    )
      return;

    const userAccess = dbUser.userFindOne.access;
    let hasUpdate = false;

    // Update the user's organization access status
    if (organizationId) {
      const orgIdNorm = normalizeEntityId(organizationId);
      const access = userAccess.organizations.find(
        item => normalizeEntityId(item?.entityId) === orgIdNorm,
      );
      if (access && access.status !== EnumUserAccessOperatorsStatus.Accepted) {
        hasUpdate = true;
        access.status = EnumUserAccessOperatorsStatus.Accepted;
      }
    }

    // Update the user's operator access status
    if (operatorEntityId) {
      const opIdNorm = normalizeEntityId(operatorEntityId);
      const access = userAccess.operators.find(
        item => normalizeEntityId(item?.entityId) === opIdNorm,
      );
      if (access && access.status !== EnumUserAccessOperatorsStatus.Accepted) {
        hasUpdate = true;
        access.status = EnumUserAccessOperatorsStatus.Accepted;
      }
    }

    // Update the user's storefront access status
    if (storefrontId) {
      const storefrontIdNorm = normalizeEntityId(storefrontId);
      const access = userAccess.storefronts.find(
        item => normalizeEntityId(item?.entityId) === storefrontIdNorm,
      );
      if (access && access.status !== EnumUserAccessOperatorsStatus.Accepted) {
        hasUpdate = true;
        access.status = EnumUserAccessOperatorsStatus.Accepted;
      }
    }

    // If either of the access statuses were updated, update the user's access
    // This will refetch the dbUser
    if (hasUpdate) {
      userUpdateMutation({
        variables: {
          id: dbUser.userFindOne._id,
          record: {
            access: userAccess,
          },
        },
      })
        .then(response => {
          if (response.errors) {
            throw new Error("Unable to update user access status");
          }
        })
        .catch(() => {
          throw new Error("Unable to update user access status");
        });
    }
  }, [
    dbUser?.userFindOne,
    organizationId,
    operatorEntityId,
    storefrontId,
    userUpdateMutation,
  ]);

  /**
   * Auto-accept all NOT_INVITED operator listings on login.
   * When an org admin adds a user as staff without sending an invite email,
   * the access status is NOT_INVITED. These listings are filtered out of
   * "My Listings" until the user visits each one individually. This effect
   * bulk-promotes them so admin-added listings appear immediately.
   */
  const hasAutoAcceptedNotInvited = useRef(false);
  useEffect(() => {
    if (!dbUser?.userFindOne || hasAutoAcceptedNotInvited.current) return;

    const userAccess = dbUser.userFindOne.access;
    const notInvitedOps = userAccess.operators.filter(
      item =>
        item?.status === EnumUserAccessOperatorsStatus.NotInvited &&
        item?.role !== EnumUserAccessOperatorsRole.NoAccess,
    );

    if (notInvitedOps.length === 0) return;

    hasAutoAcceptedNotInvited.current = true;

    const updatedOperators = userAccess.operators.map(item =>
      item?.status === EnumUserAccessOperatorsStatus.NotInvited &&
      item?.role !== EnumUserAccessOperatorsRole.NoAccess
        ? { ...item, status: EnumUserAccessOperatorsStatus.Accepted }
        : item,
    );

    userUpdateMutation({
      variables: {
        id: dbUser.userFindOne._id,
        record: {
          access: {
            ...userAccess,
            operators: updatedOperators,
          },
        },
      },
    }).catch(() => {
      hasAutoAcceptedNotInvited.current = false;
    });
  }, [dbUser?.userFindOne, userUpdateMutation]);

  // Identify the user in PostHog once loaded and register userCategory as a super property
  useEffect(() => {
    if (dbUser?.userFindOne) {
      identifyPostHog(dbUser.userFindOne);
      registerPostHog({
        userCategory: getUserCategory(dbUser.userFindOne),
      });
    }
  }, [dbUser?.userFindOne, identifyPostHog, registerPostHog]);

  const dbUserErrorMessage = dbUserError?.message;
  const opErrorMessage = opError?.message;
  const orgErrorMessage = orgError?.message;
  useEffect(() => {
    if (!dbUserErrorMessage && !opErrorMessage && !orgErrorMessage) {
      return;
    }
    trackPostHogExceptionRef.current(new Error("Unable to load the user"), {
      fileName: "AppContextProvider.tsx",
      method: "useEffect",
      message: "Error loading the user",
      extra: {
        ...(dbUserErrorMessage ? { dbUserError: dbUserErrorMessage } : {}),
        ...(opErrorMessage ? { opError: opErrorMessage } : {}),
        ...(orgErrorMessage ? { orgError: orgErrorMessage } : {}),
      },
    });
  }, [dbUserErrorMessage, opErrorMessage, orgErrorMessage]);

  const [navigationBlockerDetails, setNavigationBlockerDetails] =
    useState<NavigationBlockerDetails>({
      isNavigationBlocked: false,
      isNavigationBlockerDismissed: false,
    });

  useRegisterDirtyForm(navigationBlockerDetails.isNavigationBlocked);
  const hasDirtyForms = useHasDirtyForms();

  /**
   * Add an onbeforeunload event listener to the window object when there
   * are unsaved changes to block accidental closing of the tab/data loss
   */
  useEffect(() => {
    window.onbeforeunload = hasDirtyForms
      ? (e: Event) => {
          e.preventDefault();
          return "block";
        }
      : null;

    return () => {
      window.onbeforeunload = null;
    };
  }, [hasDirtyForms]);

  /**
   * Simple Security Check
   * - If the user is at a route as an operator, organization, or storefront,
   *   we need to check if they have access to that entity
   * - If they do not have access, we need to redirect them to the home page
   * - If they do have access, we need to allow them to access the route
   */
  const loggedInAs = useLoggedInAs();
  const isAuthorized = useMemo(() => {
    // Ensure the NotAuthorized page is NOT shown while loading the user
    if (!loggedInAs || !dbUser?.userFindOne) {
      return true;
    }

    // TNB Internal users are always authorized
    if (dbUser.userFindOne.userType === EnumUserUserType.TnbInternal) {
      return true;
    }

    // Allow Adminland ONLY to TNB Internal users
    if (loggedInAs.type === EnumOwnerType.TnbInternal) {
      return false;
    }

    // On other routes, ensure the user has access to the entity they are trying to access
    const accessType = `${loggedInAs.type.toLowerCase()}s` as
      | "operators"
      | "organizations"
      | "storefronts";

    const access = dbUser.userFindOne.access[
      accessType
    ] as UserAccessOperators[];
    const entityIdToMatch = normalizeEntityId(loggedInAs.entityId);
    return access?.some(item => {
      if (normalizeEntityId(item?.entityId) !== entityIdToMatch) {
        return false;
      }
      return (
        item.status === EnumUserAccessOperatorsStatus.Accepted ||
        item.status === EnumUserAccessOperatorsStatus.Invited
      );
    });
  }, [dbUser?.userFindOne, loggedInAs]);

  // Set loggedInAs context as super properties so they appear on every event
  useEffect(() => {
    if (loggedInAs) {
      registerPostHog({
        loggedInAsType: loggedInAs.type,
        loggedInAsEntityId: loggedInAs.entityId,
      });
      groupPostHog(loggedInAs);
    }
  }, [loggedInAs, registerPostHog, groupPostHog]);

  /**
   * For organization disabled apps, we need to ensure the user is not accessing
   * one of those apps. If we detect that the user is accessing one of those apps,
   * we will navigate them to the home page for the organization.
   */
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const pathParts = location.pathname.split("/");
    if (
      loggedInAs?.type === EnumOwnerType.Organization &&
      pathParts.length > 4
    ) {
      const app = pathParts[4]
        .replace("-", "_")
        .toUpperCase() as EnumOrganizationMetadataDisabledApps;
      const organization = organizations?.organizationsByUser?.find(
        org => org._id === loggedInAs.entityId,
      );
      if (organization?.metadata?.disabledApps?.includes(app)) {
        // Storefront can be hidden from the org home grid for operators while org-level
        // connection settings remain enabled; nested /storefront/:id routes must still work.
        if (
          app === EnumOrganizationMetadataDisabledApps.Storefront &&
          organization.metadata?.storefrontOrgConnectionsEnabled !== false
        ) {
          return;
        }
        // Navigate to the Org's home page
        const newPath = pathParts.slice(0, 4).join("/");
        navigate(newPath, { replace: true });
      }
    }
  }, [
    location.pathname,
    loggedInAs,
    organizations?.organizationsByUser,
    navigate,
  ]);

  /**
   * When the location changes, reset the navigation blocker details
   */
  useEffect(() => {
    setNavigationBlockerDetails({
      isNavigationBlocked: false,
      isNavigationBlockerDismissed: false,
    });
  }, [location.pathname]);

  /**
   * Determine if storefronts are enabled for the user
   */
  const storefrontsEnabled = useMemo(() => {
    return (
      organizations?.organizationsByUser?.some(
        organization =>
          !organization?.metadata?.disabledApps?.includes(
            EnumOrganizationMetadataDisabledApps.Storefront,
          ),
      ) ||
      // Check if the user's operator connections have the Storefront app enabled
      operatorConnections?.organizationsByIds?.some(
        organization =>
          !organization?.metadata?.disabledApps?.includes(
            EnumOrganizationMetadataDisabledApps.Storefront,
          ),
      ) ||
      // Check if the user has been invited to a storefront ONLY - enable storefronts for this user
      ((!operatorConnections ||
        operatorConnections?.organizationsByIds.length === 0) &&
      (!operators || operators?.operatorsByUser.length === 0) &&
      storefronts &&
      storefronts.storefrontsByUser.length > 0
        ? true
        : false) ||
      // No storefronts are enabled
      false
    );
  }, [
    organizations?.organizationsByUser,
    operatorConnections,
    operators,
    storefronts,
  ]);

  /**
   * Set the context value
   */
  const contextValue: AppContextValues = useMemo(
    () => ({
      isAuthorized,
      isInitialized: Object.values(initializedItems).every(
        item => item === true,
      ),
      dbUser: dbUser?.userFindOne,
      dbUserError: !!dbUserError,
      dbUserLoading: dbUserLoading ?? false,
      dbPerson: dbPerson?.personFindOne,
      dbPersonLoading: dbPersonLoading ?? false,
      operators: operators?.operatorsByUser ?? [],
      operatorConnections: operatorConnections?.organizationsByIds ?? [],
      organizations: organizations?.organizationsByUser ?? [],
      storefronts: storefronts?.storefrontsByUser ?? [],
      storefrontsEnabled,
      operatorsError: !!opError,
      operatorsLoading: opLoading ?? false,
      operatorConnectionsLoading: operatorConnectionsLoading ?? false,
      operatorConnectionsError,
      organizationsError: !!orgError,
      organizationsLoading: orgLoading ?? false,
      storefrontsError: !!sfError,
      storefrontsLoading: sfLoading ?? false,
      broadcasts: broadcasts?.broadcastByUser ?? [],
      broadcastsError: !!bcError,
      broadcastsLoading: bcLoading,
      isForcingLogout,
      navigationBlockerDetails,
      refetchDbUser,
      refetchDbPerson,
      refetchBroadcasts,
      setNavigationBlockerDetails,
    }),
    [
      isAuthorized,
      initializedItems,
      dbUser?.userFindOne,
      dbUserError,
      dbUserLoading,
      dbPerson?.personFindOne,
      dbPersonLoading,
      operators?.operatorsByUser,
      operatorConnections?.organizationsByIds,
      organizations?.organizationsByUser,
      storefronts?.storefrontsByUser,
      storefrontsEnabled,
      opError,
      opLoading,
      operatorConnectionsLoading,
      operatorConnectionsError,
      orgError,
      orgLoading,
      sfError,
      sfLoading,
      broadcasts?.broadcastByUser,
      bcError,
      bcLoading,
      isForcingLogout,
      navigationBlockerDetails,
      refetchDbUser,
      refetchDbPerson,
      refetchBroadcasts,
    ],
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};
